import { BaseService } from './BaseService.js'
import { COLLECTIONS } from '../config.js'

/**
 * Service for managing features
 */
export class FeatureService extends BaseService {
  _nextFeatureIdCache: string | null = null
  _lastFeatureIdFetched: string | null = null
  _nextFeatureIdMutex: Promise<unknown> | null = null

  constructor() {
    super(COLLECTIONS.FEATURES)
  }

  /**
   * Generate a unique clone title
   * @param {string} originalTitle - Original feature title
   * @returns {Promise<string>} Clone title with appropriate suffix
   */
  async _generateCloneTitle(originalTitle) {
    if (!originalTitle) return 'Untitled (Copy)'
    
    await this._ensureDatabaseService()
    
    // Get all features in the project to check for duplicates
    const allFeatures = await this.db.list(this.collectionName)
    
    // Extract base title
    const baseTitle = originalTitle.trim()
    
    // Try different variations until we find a unique one
    let suffix = ''
    let counter = 1
    let proposedTitle = `${baseTitle} (Copy)`
    
    // Check if this title already exists (including deleted features to avoid issues when restoring)
    while (allFeatures.some(f => f.title === proposedTitle)) {
      if (baseTitle.endsWith('(Copy)')) {
        // If base title already has (Copy), try (Copy 2), (Copy 3), etc.
        const withoutCopy = baseTitle.replace(/\(\s*Copy\s*\)\s*$/, '').trim()
        suffix = counter === 1 ? '' : ` ${counter}`
        proposedTitle = `${withoutCopy} (Copy${suffix})`
      } else if (counter === 1) {
        // First attempt, already have (Copy)
        proposedTitle = `${baseTitle} (Copy)`
      } else {
        // Subsequent attempts: (Copy 2), (Copy 3), etc.
        suffix = ` ${counter}`
        proposedTitle = `${baseTitle} (Copy${suffix})`
      }
      counter++
    }
    
    return proposedTitle
  }

  /**
   * Generate next feature ID
   * @returns {Promise<string>} Next feature ID (e.g., F00001)
   */
  async getNextFeatureId(_increment = true) {
    try {
      const { http } = await import('@/utils/httpClient')
      const ids = await http.get('/features/next-id?count=1')
      if (Array.isArray(ids) && ids.length > 0) {
        return ids[0]
      }
      return 'F00001'
    } catch (error) {
      return 'F00001'
    }
  }

  /**
   * Create a new feature
   * @param {Object} featureData - Feature data
   * @returns {Promise<Object>} Created feature
   */
  async createFeature(featureData) {
    
    // Validate that title is unique within the project (excluding deleted features)
    // Allow creating a feature with the same name as a deleted feature
    // But prevent creating if a non-deleted feature with the same name exists
    if (featureData.title) {
      await this._ensureDatabaseService()
      const allFeatures = await this.db.list(this.collectionName)
      
      // Check if a non-deleted feature with the same title already exists
      const duplicateExists = allFeatures.some(f => 
        f.project_id === featureData.project_id && 
        f.title === featureData.title &&
        !f.is_deleted && 
        !f.isDeleted
      )
      
      if (duplicateExists) {
        throw new Error(`A feature with the title "${featureData.title}" already exists. Please choose a unique title.`)
      }
    }
    
    // Generate feature ID if not provided
    if (!featureData.featureId) {
      featureData.featureId = await this.getNextFeatureId()
    }
    
    // Set the 'id' field if not provided (required by MongoDB schema)
    // Use featureId as the id, or generate a unique one
    if (!featureData.id) {
      featureData.id = featureData.featureId || `feature_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
    
    // Initialize arrays if not provided
    if (!featureData.comments) {
      featureData.comments = []
    }
    if (!featureData.activities) {
      featureData.activities = []
    }
    if (!featureData.tags) {
      featureData.tags = []
    }
    
    // Add creation activity
    const creationActivity = {
      type: 'created',
      description: 'Feature created',
      userName: featureData.created_by_name || 'Unknown',
      userId: featureData.created_by,
      timestamp: new Date()
    }
    featureData.activities.push(creationActivity)
    
    const feature = await this.create(featureData)
    
    return feature
  }

  /**
   * Update a feature and track activities
   * @param {string} id - Feature ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated feature
   */
  async updateFeature(id, updateData) {

    // Get existing feature to track changes
    const existing = await this.getById(id)
    if (!existing) {
      throw new Error('Feature not found')
    }

    // Transient activity-metadata sent by the caller (e.g. FeatureDetail.vue).
    // Keep local references and strip them from updateData so they are never persisted.
    const linkedTestRunsMeta = updateData._linkedTestRunsMeta || {}
    const linkedBugsMeta = updateData._linkedBugsMeta || {}
    const linkedSubtasksMeta = updateData._linkedSubtasksMeta || {}
    delete updateData._linkedTestRunsMeta
    delete updateData._linkedBugsMeta
    delete updateData._linkedSubtasksMeta
    
    // Validate if trying to close the feature
    if (updateData.status === 'closed' && existing.status !== 'closed') {
      
      // Check if there are open subtasks (including nested subtasks recursively)
      const allOpenSubtasks = await this.getAllOpenSubtasksRecursive(id)
      
      if (allOpenSubtasks.length > 0) {
        const openSubTaskTitles = allOpenSubtasks.map(st => st.title || st.featureId).join(', ')
        const levels = [...new Set(allOpenSubtasks.map(st => st.level))].sort()
        const levelText = levels.length > 1 ? ` across ${levels.length} level(s)` : ''
        throw new Error(`Cannot close feature: ${allOpenSubtasks.length} sub-feature(s) are still open${levelText} (${openSubTaskTitles}). Please close all sub-features first.`)
      }
      
    }
    
    const activities = existing.activities || []
    const changes = []
    
    // Track status changes
    if (updateData.status && updateData.status !== existing.status) {
      changes.push({
        field: 'status',
        from: existing.status,
        to: updateData.status
      })
      
      // Format status labels for display
      const formatStatus = (status) => {
        if (!status) return 'None'
        return status.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
      }
      
      activities.push({
        type: 'status_changed',
        description: `Status changed from ${formatStatus(existing.status)} to ${formatStatus(updateData.status)}`,
        userName: updateData.updated_by_name || 'Unknown',
        userId: updateData.updated_by || existing.updated_by,
        timestamp: new Date(),
        details: { from: existing.status, to: updateData.status }
      })
    }
    
    // Track assignee changes
    if (updateData.dev_assignee && updateData.dev_assignee !== existing.dev_assignee) {
      changes.push({
        field: 'dev_assignee',
        from: existing.dev_assignee,
        to: updateData.dev_assignee
      })
      activities.push({
        type: 'assignee_changed',
        description: `Dev assignee changed`,
        userName: updateData.updated_by_name || 'Unknown',
        userId: updateData.updated_by || existing.updated_by,
        timestamp: new Date(),
        details: { field: 'dev_assignee', from: existing.dev_assignee, to: updateData.dev_assignee }
      })
    }
    
    if (updateData.qa_assignee && updateData.qa_assignee !== existing.qa_assignee) {
      changes.push({
        field: 'qa_assignee',
        from: existing.qa_assignee,
        to: updateData.qa_assignee
      })
      activities.push({
        type: 'assignee_changed',
        description: `QA assignee changed`,
        userName: updateData.updated_by_name || 'Unknown',
        userId: updateData.updated_by || existing.updated_by,
        timestamp: new Date(),
        details: { field: 'qa_assignee', from: existing.qa_assignee, to: updateData.qa_assignee }
      })
    }
    
    const userName = updateData.updated_by_name || 'Unknown'
    const userId = updateData.updated_by || existing.updated_by

    // Track test run linking / unlinking
    if (updateData.test_run_ids) {
      const oldTestRunIds = existing.test_run_ids || []
      const newTestRunIds = updateData.test_run_ids || []
      const addedTestRunIds = newTestRunIds.filter(id => !oldTestRunIds.includes(id))
      const removedTestRunIds = oldTestRunIds.filter(id => !newTestRunIds.includes(id))

      const testRunLabel = (trId) => {
        const meta = linkedTestRunsMeta[trId]
        return meta?.title || meta?.name || meta?.testrun_id || trId
      }

      addedTestRunIds.forEach((trId) => {
        activities.push({
          type: 'test_run_linked',
          description: `linked test run "${testRunLabel(trId)}"`,
          userName,
          userId,
          timestamp: new Date(),
          details: {
            entity: 'test_run',
            id: trId,
            testrun_id: linkedTestRunsMeta[trId]?.testrun_id,
            title: linkedTestRunsMeta[trId]?.title || linkedTestRunsMeta[trId]?.name
          }
        })
      })

      removedTestRunIds.forEach((trId) => {
        activities.push({
          type: 'test_run_unlinked',
          description: `unlinked test run "${testRunLabel(trId)}"`,
          userName,
          userId,
          timestamp: new Date(),
          details: {
            entity: 'test_run',
            id: trId,
            testrun_id: linkedTestRunsMeta[trId]?.testrun_id,
            title: linkedTestRunsMeta[trId]?.title || linkedTestRunsMeta[trId]?.name
          }
        })
      })
    }

    if (updateData.bug_ids) {
      const oldBugIds = existing.bug_ids || []
      const newBugIds = updateData.bug_ids || []
      const addedBugIds = newBugIds.filter(id => !oldBugIds.includes(id))
      const removedBugIds = oldBugIds.filter(id => !newBugIds.includes(id))

      const bugLabel = (bId) => {
        const meta = linkedBugsMeta[bId]
        return meta?.title || meta?.bugId || bId
      }

      addedBugIds.forEach((bId) => {
        activities.push({
          type: 'bug_linked',
          description: `linked bug "${bugLabel(bId)}"`,
          userName,
          userId,
          timestamp: new Date(),
          details: {
            entity: 'bug',
            id: bId,
            bugId: linkedBugsMeta[bId]?.bugId,
            title: linkedBugsMeta[bId]?.title
          }
        })
      })

      removedBugIds.forEach((bId) => {
        activities.push({
          type: 'bug_unlinked',
          description: `unlinked bug "${bugLabel(bId)}"`,
          userName,
          userId,
          timestamp: new Date(),
          details: {
            entity: 'bug',
            id: bId,
            bugId: linkedBugsMeta[bId]?.bugId,
            title: linkedBugsMeta[bId]?.title
          }
        })
      })
    }

    if (updateData.sub_task_ids) {
      const oldSubtaskIds = existing.sub_task_ids || []
      const newSubtaskIds = updateData.sub_task_ids || []
      const addedSubtaskIds = newSubtaskIds.filter(id => !oldSubtaskIds.includes(id))
      const removedSubtaskIds = oldSubtaskIds.filter(id => !newSubtaskIds.includes(id))

      const subtaskLabel = (sId) => {
        const meta = linkedSubtasksMeta[sId]
        return meta?.title || meta?.featureId || sId
      }

      addedSubtaskIds.forEach((sId) => {
        activities.push({
          type: 'subtask_linked',
          description: `linked subtask "${subtaskLabel(sId)}"`,
          userName,
          userId,
          timestamp: new Date(),
          details: {
            entity: 'subtask',
            id: sId,
            featureId: linkedSubtasksMeta[sId]?.featureId,
            title: linkedSubtasksMeta[sId]?.title
          }
        })
      })

      removedSubtaskIds.forEach((sId) => {
        activities.push({
          type: 'subtask_unlinked',
          description: `unlinked subtask "${subtaskLabel(sId)}"`,
          userName,
          userId,
          timestamp: new Date(),
          details: {
            entity: 'subtask',
            id: sId,
            featureId: linkedSubtasksMeta[sId]?.featureId,
            title: linkedSubtasksMeta[sId]?.title
          }
        })
      })
    }


    // Track other field changes (title, description, priority, dates, links)
    const trackedFields = ['title', 'description', 'priority', 'start_date', 'due_date', 'pr_link', 'brd_link', 'design_link']
    const linkArrayFields = new Set(['pr_link', 'brd_link', 'design_link'])
    const fieldLabels = {
      'title': 'Title',
      'description': 'Description',
      'priority': 'Priority',
      'start_date': 'Start date',
      'due_date': 'Due date',
      'pr_link': 'PR link',
      'brd_link': 'BRD link',
      'design_link': 'Design link'
    }

    const valuesEqual = (a: any, b: any) => {
      if (Array.isArray(a) || Array.isArray(b)) {
        const aArr = Array.isArray(a) ? a : (a == null ? [] : [a])
        const bArr = Array.isArray(b) ? b : (b == null ? [] : [b])
        return JSON.stringify(aArr) === JSON.stringify(bArr)
      }
      return a === b
    }

    for (const field of trackedFields) {
      if (updateData[field] === undefined) continue

      const isLinkArray = linkArrayFields.has(field)
      const fromValue = isLinkArray && !Array.isArray(existing[field])
        ? (existing[field] ? String(existing[field]).split(',').map(s => s.trim()).filter(Boolean) : [])
        : existing[field]

      if (!valuesEqual(updateData[field], fromValue)) {
        changes.push({
          field: field,
          from: fromValue,
          to: updateData[field]
        })

        activities.push({
          type: 'field_updated',
          description: `${fieldLabels[field]} updated`,
          userName: updateData.updated_by_name || 'Unknown',
          userId: updateData.updated_by || existing.updated_by,
          timestamp: new Date(),
          details: { field: field, from: fromValue, to: updateData[field] }
        })
      }
    }
    
    // Add activities to update data
    updateData.activities = activities
    updateData.updated_at = new Date()
    
    const updated = await this.update(id, updateData)
    
    return updated
  }

  /**
   * Get features by project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of features
   */
  async getByProject(projectId, additionalFilters = {}) {
    await this._ensureDatabaseService()

    // Get all features for the project.
    // Backend now defaults to is_deleted: { $ne: true } so no client-side filtering needed.
    const conditions = [
      { field: 'project_id', operator: '==', value: projectId }
    ]

    // Add additional filters if provided
    Object.keys(additionalFilters).forEach(key => {
      if (additionalFilters[key] !== undefined) {
        conditions.push({ field: key, operator: '==', value: additionalFilters[key] })
      }
    })

    return await this.db.query(this.collectionName, conditions)
  }

  /**
   * Soft delete a feature
   * @param {string} id - Feature ID
   * @param {string} deletedBy - User ID who deleted the feature
   * @param {string} deletedByName - User name who deleted the feature
   * @returns {Promise<Object>} Updated feature
   */
  async softDelete(id, deletedBy, deletedByName = 'Unknown') {
    try {
      // Use dedicated soft-delete endpoint (bypasses update() safeguard)
      // Note: Parent-child unlinking and recursive child deletion
      // should be handled by the backend service if needed
      const { http } = await import('@/utils/httpClient')
      const response = await http.post(`/features/${id}/soft-delete`, {
        deletedBy,
        deletedByName
      })
      return response
    } catch (error) {
      console.error('❌ Error soft deleting feature:', error)
      throw error
    }
  }

  /**
   * Restore a soft-deleted feature
   * @param {string} id - Feature ID
   * @param {string} restoredBy - User ID who restored the feature
   * @param {string} restoredByName - User name who restored the feature
   * @param {boolean} unlinkFromParent - If true, unlink from parent if parent is deleted
   * @returns {Promise<Object>} Restored feature
   */
  async restore(id, restoredBy, restoredByName = 'Unknown', unlinkFromParent = false) {
    
    // Get current feature to add activity
    const currentFeature = await this.getById(id)
    if (!currentFeature) {
      throw new Error('Feature not found')
    }

    // Validate that restoring this feature won't create a duplicate title
    // If a non-deleted feature with the same title already exists, prevent restoration
    if (currentFeature.title) {
      await this._ensureDatabaseService()
      const allFeatures = await this.db.list(this.collectionName)
      
      // Check if a non-deleted feature with the same title already exists (excluding the current feature being restored)
      const duplicateExists = allFeatures.some(f => 
        f.id !== id &&
        f.project_id === currentFeature.project_id && 
        f.title === currentFeature.title &&
        !f.is_deleted && 
        !f.isDeleted
      )
      
      if (duplicateExists) {
        throw new Error(`Cannot restore feature: A feature with the title "${currentFeature.title}" already exists. Please rename the existing feature or delete it first.`)
      }
    }

    // Check if parent feature is deleted (with safety check to prevent infinite recursion)
    let parentToRestore = null
    if (currentFeature.parent_id || currentFeature.parentId) {
      const parentId = currentFeature.parent_id || currentFeature.parentId
      
      // Safety check: prevent infinite recursion by ensuring we don't restore the same parent we're already restoring
      // This can happen if both parent and child are in the process of being restored
      try {
      const parentFeature = await this.getById(parentId)
      
      if (parentFeature && (parentFeature.is_deleted || parentFeature.isDeleted)) {
        parentToRestore = parentFeature
        
        // If unlinkFromParent is false, restore the parent first
        // If unlinkFromParent is true, remove the parent reference and restore as orphan
        if (!unlinkFromParent) {
          await this.restore(parentId, restoredBy, restoredByName, false)
        }
        }
      } catch (error) {
        // Continue with restoration even if parent check fails
      }
    }

    // Add restore activity
    const activities = currentFeature.activities || []
    if (restoredBy) {
      activities.push({
        type: 'restored',
        description: 'Feature restored',
        userName: restoredByName,
        userId: restoredBy,
        timestamp: new Date()
      })
    }
    
    // If unlinkFromParent is true and parent is deleted, add unlink activity
    if (unlinkFromParent && parentToRestore && restoredBy) {
      const parentId = currentFeature.parent_id || currentFeature.parentId
      const parentFeatureId = parentToRestore.featureId || parentId
      activities.push({
        type: 'unlinked',
        description: `Unlinked from parent feature ${parentFeatureId} (restored as standalone)`,
        userName: restoredByName,
        userId: restoredBy,
        timestamp: new Date()
      })
    }
    
    const updateData = {
      is_deleted: false,
      deleted_at: null,
      deleted_by: null,
      updated_at: new Date(),
      activities: activities
    }
    
    // If unlinkFromParent is true and parent is deleted, remove parent reference
    if (unlinkFromParent && parentToRestore) {
      updateData.parent_id = null
      updateData.parentId = null
      
      // Also remove this feature from parent's child_features array
      const parentId = currentFeature.parent_id || currentFeature.parentId
      if (parentId) {
        try {
          const updatedParent = await this.getById(parentId)
          if (updatedParent && updatedParent.child_features) {
            const updatedChildFeatures = updatedParent.child_features.filter(
              child => child.id !== id
            )
            
            // Add activity to parent about child being unlinked
            const parentActivities = updatedParent.activities || []
            const childFeatureId = currentFeature.featureId || id
            parentActivities.push({
              type: 'child_unlinked',
              description: `Child feature ${childFeatureId} unlinked and restored separately`,
              userName: restoredByName,
              userId: restoredBy,
              timestamp: new Date()
            })
            
            await this.update(parentId, {
              child_features: updatedChildFeatures,
              activities: parentActivities,
              updated_at: new Date()
            })
          }
        } catch (error) {
          // Continue even if error
        }
      }
    }
    
    const result = await this.update(id, updateData)
    
    // If this feature has a parent that is NOT deleted, add it back to parent's child_features
    if (currentFeature.parent_id || currentFeature.parentId) {
      const parentId = currentFeature.parent_id || currentFeature.parentId
      try {
        const parent = await this.getById(parentId)
        if (parent && !parent.is_deleted && !parent.isDeleted) {
          // Parent is not deleted - add this child back to parent's child_features array
          const existingChildFeatures = parent.child_features || []
          const existsInArray = existingChildFeatures.some(cf => cf.id === id)
          
          if (!existsInArray) {
            const updatedChildFeatures = [
              ...existingChildFeatures,
              {
                id: currentFeature.id,
                featureId: currentFeature.featureId,
                title: currentFeature.title,
                status: currentFeature.status
              }
            ]
            
            const parentActivities = parent.activities || []
            if (restoredBy) {
              parentActivities.push({
                type: 'child_restored',
                description: `Child feature ${currentFeature.featureId || id} restored`,
                userName: restoredByName,
                userId: restoredBy,
                timestamp: new Date()
              })
            }
            
            await this.update(parentId, {
              child_features: updatedChildFeatures,
              activities: parentActivities,
              updated_at: new Date()
            })
          }
        }
      } catch (error) {
        // Continue with restoration even if parent update fails
      }
    }
    
    // Cascade-restore deleted descendants. Match by deleted_at timestamp so
    // we only resurrect children that were taken down with this feature, not
    // ones deleted independently earlier. Skip when unlinking (the user
    // explicitly chose to detach from the deleted parent context).
    const originalDeletedAt = currentFeature.deleted_at || currentFeature.deletedAt
    if (!unlinkFromParent && originalDeletedAt) {
      const CASCADE_WINDOW_MS = 2000
      const parentDeletedMs = new Date(originalDeletedAt).getTime()

      try {
        await this._ensureDatabaseService()
        const children = await this.findBy('parent_id', id, {
          additionalFilters: [['include_deleted', '==', 'true']]
        })

        const cascadedChildren = (children || []).filter(child => {
          if (!(child.is_deleted || child.isDeleted)) return false
          const childDeletedAt = child.deleted_at || child.deletedAt
          if (!childDeletedAt) return false
          return Math.abs(new Date(childDeletedAt).getTime() - parentDeletedMs) <= CASCADE_WINDOW_MS
        })

        for (const child of cascadedChildren) {
          try {
            // Recursively restore each cascade-deleted child. Each recursive
            // call will run its own cascade lookup for its descendants.
            await this.restore(child.id, restoredBy, restoredByName, false)
          } catch (error) {
            console.error(`Error cascade-restoring child feature ${child.id}:`, error)
          }
        }
      } catch (error) {
        console.error(`Error finding cascade-deleted children for ${id}:`, error)
      }
    }

    return result
  }

  /**
   * Clone a feature
   * @param {string} id - Feature ID to clone
   * @param {string} clonedBy - User ID who cloned the feature
   * @param {string} clonedByName - User name who cloned the feature
   * @param {boolean} withChildren - Whether to clone children as well (if feature has children)
   * @param {boolean} cloneAsStandalone - Whether to clone as standalone feature (if feature has parent)
   * @returns {Promise<Object>} Cloned feature
   */
  async cloneFeature(id, clonedBy, clonedByName = 'Unknown', withChildren = false, cloneAsStandalone = true) {
    const originalFeature = await this.getById(id)
    if (!originalFeature) {
      throw new Error('Feature not found')
    }

    // If cloning with children, first fetch ALL descendant features in a single query
    let allDescendants = []
    if (withChildren) {
      allDescendants = await this._getAllDescendants(id)
    }

    // Generate new feature ID for the parent
    const newFeatureId = await this.getNextFeatureId(true)

    // Determine parent relationship
    let parentId = null
    let parentIdField = null
    
    // If feature has a parent and we're NOT cloning as standalone, keep the parent relationship
    const hasParent = originalFeature.parent_id || originalFeature.parentId
    if (hasParent && !cloneAsStandalone) {
      parentId = originalFeature.parent_id || originalFeature.parentId
      parentIdField = parentId
    }

    // Create clone data
    const clonedData = {
      featureId: newFeatureId,
      title: await this._generateCloneTitle(originalFeature.title),
      description: originalFeature.description,
      status: originalFeature.status, // Keep same status
      priority: originalFeature.priority,
      project_id: originalFeature.project_id,
      dev_assignee: originalFeature.dev_assignee,
      qa_assignee: originalFeature.qa_assignee,
      start_date: originalFeature.start_date,
      due_date: originalFeature.due_date,
      estimate_hours: originalFeature.estimate_hours,
      created_by: clonedBy,
      created_by_name: clonedByName,
      created_at: new Date(),
      updated_at: new Date(),
      parent_id: parentIdField,
      parentId: parentId,
      comments: [],
      activities: [{
        type: 'cloned',
        description: `Cloned from ${originalFeature.featureId}`,
        userName: clonedByName,
        userId: clonedBy,
        timestamp: new Date()
      }]
    }

    const clonedFeature = await this.createFeature(clonedData)

    // If withChildren is true, clone all children using preloaded descendants
    if (withChildren && allDescendants.length > 0) {
      await this.cloneChildrenFromPreloaded(id, clonedFeature.id, allDescendants, clonedBy, clonedByName)
    }

     return clonedFeature
  }

  /**
   * Get all descendants of a feature in a single query
   * @param {string} parentId - Parent feature ID
   * @returns {Promise<Array>} Array of all descendant features
   */
  async _getAllDescendants(parentId) {
    try {
      await this._ensureDatabaseService()
      
      // Get the original parent feature
      const parent = await this.getById(parentId)
      if (!parent) return []
      
      // Get all features in the project
      const allFeatures = await this.findBy('project_id', parent.project_id)
      
      // Build a simple tree structure to identify descendants
      const featuresById = new Map()
      allFeatures.forEach(f => featuresById.set(f.id, f))
      
      const descendants = []
      const findDescendants = (id) => {
        allFeatures.forEach(feature => {
          const parentId = feature.parent_id || feature.parentId
          if (parentId === id) {
            descendants.push(feature)
            // Recursively find children of this descendant
            findDescendants(feature.id)
          }
        })
      }
      
      findDescendants(parentId)
      
      return descendants
    } catch (error) {
      console.error('Error getting all descendants:', error)
      return []
    }
  }

  /**
   * Clone children features from preloaded data (no duplicate API calls)
   * @param {string} originalParentId - Original parent feature ID
   * @param {string} newParentId - New cloned parent feature ID
   * @param {Array} allDescendants - All descendant features to process
   * @param {string} clonedBy - User ID who cloned the feature
   * @param {string} clonedByName - User name who cloned the feature
   */
  async cloneChildrenFromPreloaded(originalParentId, newParentId, allDescendants, clonedBy, clonedByName = 'Unknown') {
    try {
      if (!allDescendants || allDescendants.length === 0) {
        return new Map()
      }

      // Collect ALL features to create with their hierarchy info
      const featuresToCreate = []
      
      // First, populate the cache
      await this.getNextFeatureId(true)
      
      // Build a map to track feature ID mapping
      const tempIdToFeature = new Map() // Map: tempId -> { originalId, data, tempParentId }
      let tempIdCounter = 0
      
      const collectFeatures = async (parentId) => {
        // Find direct children
        const children = allDescendants.filter(f => {
          const pid = f.parent_id || f.parentId
          return pid === parentId
        })
        
        for (const child of children) {
          const newChildFeatureId = this._nextFeatureIdCache || 'F00001'
          
          // Generate next ID for this child
          const currentNum = parseInt(newChildFeatureId.substring(1))
          this._nextFeatureIdCache = 'F' + (currentNum + 1).toString().padStart(5, '0')
          
          // Create temporary ID for linking
          const tempId = `__temp_${tempIdCounter++}`
          
          const clonedChildData = {
            featureId: newChildFeatureId,
            title: await this._generateCloneTitle(child.title),
            description: child.description,
            status: child.status,
            priority: child.priority,
            project_id: child.project_id,
            dev_assignee: child.dev_assignee,
            qa_assignee: child.qa_assignee,
            start_date: child.start_date,
            due_date: child.due_date,
            estimate_hours: child.estimate_hours,
            parent_id: null, // Will be set based on parent's temp ID
            parentId: null,
            created_by: clonedBy,
            created_by_name: clonedByName,
            created_at: new Date(),
            updated_at: new Date(),
            comments: [],
            activities: [{
              type: 'cloned',
              description: `Cloned from ${child.featureId}`,
              userName: clonedByName,
              userId: clonedBy,
              timestamp: new Date()
            }]
          }
          
          tempIdToFeature.set(tempId, {
            originalId: child.id,
            data: clonedChildData,
            tempParentId: parentId === originalParentId ? newParentId : `__temp_parent_${child.parent_id || child.parentId}`
          })
          
          // Recursively collect grandchildren
          await collectFeatures(child.id)
        }
      }
      
      // Collect all features
      await collectFeatures(originalParentId)
      
      // Now we need to resolve parent references based on original IDs
      const resolvedFeatures = []
      const originalToTempMap = new Map()
      
      // First pass: map original IDs to feature objects
      tempIdToFeature.forEach((feature, tempId) => {
        originalToTempMap.set(feature.originalId, feature)
      })
      
      // Second pass: resolve parent links
      tempIdToFeature.forEach((feature, tempId) => {
        // Set parent ID based on what the parent's tempId should map to
        const parentFeature = originalToTempMap.get(feature.tempParentId)
        if (parentFeature) {
          feature.data.parent_id = parentFeature.data.featureId
          feature.data.parentId = parentFeature.data.featureId
        } else {
          // Parent is the cloned parent feature
          feature.data.parent_id = newParentId
          feature.data.parentId = newParentId
        }
        
        resolvedFeatures.push({ originalId: feature.originalId, data: feature.data })
      })
      
      // Batch create all features in a single call
      const idMapping = new Map()
      if (resolvedFeatures.length > 0) {
        const operations = resolvedFeatures.map(f => ({
          type: 'create',
          collection: this.collectionName,
          data: f.data
        }))
        
        const results = await this.batch(operations)
        
        // Map original IDs to new IDs
        if (results && Array.isArray(results)) {
          results.forEach((result, index) => {
            if (result.success && resolvedFeatures[index]) {
              idMapping.set(resolvedFeatures[index].originalId, result.result.id)
            }
          })
        }
      }
      
      return idMapping
    } catch (error) {
      console.error('Error in cloneChildrenFromPreloaded:', error)
      throw error
    }
  }

  /**
   * Clone children features recursively (old method - kept for backward compatibility)
   * @param {string} originalParentId - Original parent feature ID
   * @param {string} newParentId - New cloned parent feature ID
   * @param {string} clonedBy - User ID who cloned the feature
   * @param {string} clonedByName - User name who cloned the feature
   */
  async cloneChildren(originalParentId, newParentId, clonedBy, clonedByName = 'Unknown') {
    await this._ensureDatabaseService()
    
    // Find all children of the original parent
    const children = await this.findBy('parent_id', originalParentId)
    
    if (!children || children.length === 0) {
      return
    }

    // Map original child ID to new child ID
    const childIdMap = new Map()

    // Clone each child
    for (const child of children) {
      const newChildFeatureId = await this.getNextFeatureId(true)
      
      const clonedChildData = {
        featureId: newChildFeatureId,
        title: await this._generateCloneTitle(child.title),
        description: child.description,
        status: child.status, // Keep same status
        priority: child.priority,
        project_id: child.project_id,
        dev_assignee: child.dev_assignee,
        qa_assignee: child.qa_assignee,
        start_date: child.start_date,
        due_date: child.due_date,
        estimate_hours: child.estimate_hours,
        parent_id: newParentId, // Link to cloned parent
        parentId: newParentId,
        created_by: clonedBy,
        created_by_name: clonedByName,
        created_at: new Date(),
        updated_at: new Date(),
        comments: [],
        activities: [{
          type: 'cloned',
          description: `Cloned from ${child.featureId}`,
          userName: clonedByName,
          userId: clonedBy,
          timestamp: new Date()
        }]
      }

      const clonedChild = await this.createFeature(clonedChildData)
      childIdMap.set(child.id, clonedChild.id)

      // Recursively clone grandchildren
      await this.cloneChildren(child.id, clonedChild.id, clonedBy, clonedByName)
    }

    return childIdMap
  }

  /**
   * Get deleted features by project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Deleted features
   */
  async getDeletedByProject(projectId) {
    await this._ensureDatabaseService()

    // Get deleted features for the project via server-side filter
    const conditions = [
      { field: 'project_id', operator: '==', value: projectId },
      { field: 'is_deleted', operator: '==', value: 'true' }
    ]
    return await this.db.query(this.collectionName, conditions)
  }

  /**
   * Get sub-tasks of a feature
   * @param {string} featureId - Feature ID
   * @returns {Promise<Array>} Array of sub-tasks
   */
  async getSubTasks(featureId) {
    const subTasks = await this.findBy('parent_id', featureId)
    return subTasks
  }

  /**
   * Get all open subtasks recursively (including nested subtasks at all levels)
   * @param {string} featureId - Feature ID
   * @param {number} level - Current level (for tracking depth)
   * @returns {Promise<Array>} Array of all open subtasks with level information
   */
  async getAllOpenSubtasksRecursive(featureId, level = 1) {
    const directSubtasks = await this.getSubTasks(featureId)
    let allOpenSubtasks = []
    
    for (const subtask of directSubtasks) {
      // Skip deleted subtasks
      if (subtask.is_deleted) {
        continue
      }
      
      // If this subtask is open, add it to the list
      if (subtask.status !== 'closed') {
        allOpenSubtasks.push({
          ...subtask,
          level: level
        })
      }
      
      // Recursively check this subtask's children (even if this subtask is closed)
      const nestedOpenSubtasks = await this.getAllOpenSubtasksRecursive(subtask.id, level + 1)
      allOpenSubtasks = allOpenSubtasks.concat(nestedOpenSubtasks)
    }
    
    return allOpenSubtasks
  }

  /**
   * Add comment to feature
   * @param {string} featureId - Feature ID
   * @param {string} userId - User ID
   * @param {string} userName - User name
   * @param {string} commentText - Comment text
   * @returns {Promise<Object>} Updated feature
   */
  async addComment(featureId, userId, userName, commentText) {
    
    const feature = await this.getById(featureId)
    if (!feature) {
      throw new Error('Feature not found')
    }
    
    const newComment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      user_id: userId,
      user_name: userName,
      comment: commentText,
      timestamp: new Date(),
      created_at: new Date()
    }
    
    const comments = Array.isArray(feature.comments) ? [...feature.comments] : []
    comments.push(newComment)
    
    const activities = feature.activities || []
    activities.push({
      type: 'comment_added',
      description: `${userName} added a comment`,
      userName: userName,
      userId: userId,
      timestamp: new Date(),
      details: {
        commentId: newComment.id,
        commentPreview: commentText.substring(0, 100)
      }
    })
    
    await this.update(featureId, {
      comments: comments,
      activities: activities,
      updated_at: new Date()
    })
    
    return await this.getById(featureId)
  }

  /**
   * Get comments for a feature
   * @param {string} featureId - Feature ID
   * @returns {Promise<Array>} Array of comments
   */
  async getComments(featureId) {
    const feature = await this.getById(featureId)
    if (!feature) {
      throw new Error('Feature not found')
    }
    
    const comments = feature.comments || []
    return comments.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.created_at).getTime()
      const dateB = new Date(b.timestamp || b.created_at).getTime()
      return dateA - dateB // Oldest first - reverse order like ClickUp
    })
  }

  /**
   * Delete comment
   * @param {string} featureId - Feature ID
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} Updated feature
   */
  async deleteComment(featureId, commentId, userId, userName) {
    const feature = await this.getById(featureId)
    if (!feature) {
      throw new Error('Feature not found')
    }
    
    const comments = (feature.comments || []).filter(comment => comment.id !== commentId)

    const activities = feature.activities || []
    activities.push({
      type: 'comment_deleted',
      description: `${userName} deleted a comment`,
      userName: userName,
      userId: userId,
      timestamp: new Date(),
      details: {
        commentId: commentId,
      }
    })
    
    await this.update(featureId, {
      comments: comments,
      updated_at: new Date(),
      activities: activities
    })
    
    return await this.getById(featureId)
  }

  /**
   * Update comment
   * @param {string} featureId - Feature ID
   * @param {string} commentId - Comment ID
   * @param {string} newCommentText - New comment text
   * @returns {Promise<Object>} Updated feature
   */
  async updateComment(featureId, commentId, newCommentText, userId, userName) {
    const feature = await this.getById(featureId)
    if (!feature) {
      throw new Error('Feature not found')
    }
    
    const comments = feature.comments || []
    const commentIndex = comments.findIndex(comment => comment.id === commentId)
    
    if (commentIndex === -1) {
      throw new Error('Comment not found')
    }
    
    comments[commentIndex].comment = newCommentText
    comments[commentIndex].edited_at = new Date()
    comments[commentIndex].is_edited = true

    const activities = feature.activities || []
    activities.push({
      type: 'comment_updated',
      description: `${userName} updated a comment`,
      userName: userName,
      userId: userId,
      timestamp: new Date(),
      details: {
        commentId: commentId,
        commentPreview: newCommentText.substring(0, 100)
      }
    })
    
    await this.update(featureId, {
      comments: comments,
      updated_at: new Date(),
      activities: activities
    })
    
    return await this.getById(featureId)
  }

  /**
   * Validate feature closure
   * @param {string} featureId - Feature ID
   * @returns {Promise<Object>} Validation result
   */
  async validateClosure(featureId) {
    const feature = await this.getById(featureId)
    if (!feature) {
      throw new Error('Feature not found')
    }
    
    const issues = []
    
    // Check if all sub-tasks are closed
    const subTasks = await this.getSubTasks(featureId)
    const openSubTasks = subTasks.filter(st => st.status !== 'closed')
    if (openSubTasks.length > 0) {
      issues.push(`${openSubTasks.length} sub-feature(s) are still open`)
    }
    
    // Check if there are open bugs linked to the feature (supports both old test_run_id and new test_run_ids)
    const testRunIds = feature.test_run_ids || (feature.test_run_id ? [feature.test_run_id] : [])
    
    if (testRunIds.length > 0) {
      const { testRunService, bugService } = await import('./index.js')
      
      // Check bugs across all test runs
      for (const testRunId of testRunIds) {
        const testRun = await testRunService.getById(testRunId)
        
        if (testRun) {
          const bugs = await bugService.getByProject(feature.project_id)
          const featureBugs = bugs.filter(bug => 
            bug.test_case_ids && bug.test_case_ids.some(tcId => 
              testRun.test_cases && testRun.test_cases.some(tc => tc.testCaseId === tcId)
            )
          )
          const openBugs = featureBugs.filter(bug => 
            bug.status !== 'closed' && bug.status !== 'resolved'
          )
          
          if (openBugs.length > 0) {
            issues.push(`${openBugs.length} bug(s) related to this feature are still open`)
            break // No need to check other test runs if we found open bugs
          }
        }
      }
    }
    
    return {
      canClose: issues.length === 0,
      issues: issues
    }
  }

  /**
   * Close feature
   * @param {string} featureId - Feature ID
   * @param {string} userId - User ID
   * @param {string} userName - User name
   * @returns {Promise<Object>} Updated feature
   */
  async closeFeature(featureId, userId, userName) {
    // Validate closure
    const validation = await this.validateClosure(featureId)
    if (!validation.canClose) {
      throw new Error(`Cannot close feature: ${validation.issues.join(', ')}`)
    }
    
    return await this.updateFeature(featureId, {
      status: 'closed',
      updated_by: userId,
      updated_by_name: userName
    })
  }

  /**
   * Get feature coverage metrics
   * @param {string} featureId - Feature ID
   * @returns {Promise<Object>} Coverage metrics
   */
  async getCoverageMetrics(featureId) {
    try {
      
      const feature = await this.getById(featureId)
      
      // Support both old test_run_id and new test_run_ids array
      const testRunIds = feature?.test_run_ids || (feature?.test_run_id ? [feature.test_run_id] : [])
      
      if (!feature || testRunIds.length === 0) {
        return {
          totalTestCases: 0,
          automatedTestCases: 0,
          automationCoverage: 0,
          testCaseCoverage: 0
        }
      }
      
      
      const { testRunService, testCaseService } = await import('./index.js')
      
      // Aggregate test cases from all linked test runs
      let allTestCaseIds = []
      
      for (const testRunId of testRunIds) {
        const testRun = await testRunService.getById(testRunId)
        
        if (testRun && testRun.test_cases && testRun.test_cases.length > 0) {
          // Extract test case IDs - handle both direct IDs and objects with testCaseId property
          const testCaseIds = testRun.test_cases.map(tc => 
            typeof tc === 'string' ? tc : (tc.testCaseId || tc.id)
          ).filter(id => id)
          
          allTestCaseIds.push(...testCaseIds)
        }
      }
      
      // Remove duplicates
      allTestCaseIds = [...new Set(allTestCaseIds)]
      
      
      if (allTestCaseIds.length === 0) {
        return {
          totalTestCases: 0,
          automatedTestCases: 0,
          automationCoverage: 0,
          testCaseCoverage: 0
        }
      }
      
      const testCases = await Promise.all(
        allTestCaseIds.map(id => testCaseService.getById(id).catch(err => {
          return null
        }))
      )
      
      const validTestCases = testCases.filter(tc => tc !== null)
      
      const automatedCount = validTestCases.filter(tc => 
        tc.automation_status === 'automated'
      ).length
      
      
      const metrics = {
        totalTestCases: validTestCases.length,
        automatedTestCases: automatedCount,
        automationCoverage: validTestCases.length > 0 
          ? Math.round((automatedCount / validTestCases.length) * 100) 
          : 0,
        testCaseCoverage: validTestCases.length
      }
      
      return metrics
    } catch (error) {
      return {
        totalTestCases: 0,
        automatedTestCases: 0,
        automationCoverage: 0,
        testCaseCoverage: 0
      }
    }
  }
}

export const featureService = new FeatureService()
